// ─── Feuille : créer / éditer un REPAS (composition) ──────────────
// Un Repas = plusieurs recettes de la bibliothèque (chacune pour un nombre de
// personnes propre) + des extras hors recette. `recettes` (bibliothèque
// complète) est passé par l'écran, déjà chargé.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, ChoixPuces, Stepper, stylesForm, useUI, useT } from '@mop/core';

const EMOJIS_REPAS = ['🍽️', '🥗', '🍲', '🍕', '🥪', '🍜', '🍳', '🥘', '🍱', '🌮'];
const VIDE = { nom: '', icone: '', personnes: 4, recettes: [], extras: [] };

export default function FeuilleRepas({ visible, initial, recettes = [], onEnregistrer, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [f, setF] = useState(VIDE);
  const [picker, setPicker] = useState(false);

  useEffect(() => {
    if (!visible) return;
    const r = initial || {};
    setF({
      ...VIDE, ...r,
      personnes: Math.max(1, Number(r.personnes) || 4),
      recettes: r.recettes || [],
      extras: r.extras || [],
    });
    setPicker(false);
  }, [visible, initial]);

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const valide = !!(f.nom || '').trim();
  const nomRecette = (id) => (recettes.find(r => r.id === id) || {}).nom || t('repasPlan.recetteSupprimee');

  const disponibles = recettes.filter(r => !f.recettes.some(x => x.recetteId === r.id));
  const ajouterRecette = (recetteId) => {
    setF(x => ({ ...x, recettes: [...x.recettes, { recetteId, personnes: x.personnes }] }));
    setPicker(false);
  };
  const retirerRecette = (recetteId) => setF(x => ({ ...x, recettes: x.recettes.filter(r => r.recetteId !== recetteId) }));
  const majPersonnesRecette = (recetteId, personnes) =>
    setF(x => ({ ...x, recettes: x.recettes.map(r => r.recetteId === recetteId ? { ...r, personnes } : r) }));

  const setExtra = (i, k, v) => setF(x => ({ ...x, extras: x.extras.map((e, j) => j === i ? { ...e, [k]: v } : e) }));
  const ajouterExtra = () => setF(x => ({ ...x, extras: [...x.extras, { nom: '', quantite: '', unite: '', prix: '' }] }));
  const retirerExtra = (i) => setF(x => ({ ...x, extras: x.extras.filter((_, j) => j !== i) }));

  return (
    <Feuille visible={visible} titre={initial?.id ? t('repas.editerRepas') : t('repas.nouveauRepas')}
      emoji={f.icone || '🍽️'} hauteur={0.9} onFermer={onFermer}
      action={{ icone: initial?.id ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !valide, onPress: () => onEnregistrer(f) }}>

      <Text style={s.lbl}>{t('commun.nom')}</Text>
      <TextInput style={s.input} value={f.nom} onChangeText={v => set('nom', v)}
        placeholder={t('repas.phNomRepas')} placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('commun.emoji')}</Text>
      <View style={s.emojis}>
        {EMOJIS_REPAS.map(e => (
          <TouchableOpacity key={e} style={[s.emoji, f.icone === e && s.emojiSel]} activeOpacity={0.7} onPress={() => set('icone', e)}>
            <Text style={{ fontSize: 20 }}>{e}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <View style={s.row}>
        <Text style={s.lbl}>{t('repas.pour')}</Text>
      </View>
      <Stepper valeur={f.personnes} onMoins={() => set('personnes', Math.max(1, f.personnes - 1))}
        onPlus={() => set('personnes', f.personnes + 1)} apres={<Text style={s.apres}>{t('repas.personnes')}</Text>} />

      <Text style={s.lbl}>{t('repas.recettes')}</Text>
      {f.recettes.length === 0 && <Text style={s.vide}>{t('repas.aucuneRecette')}</Text>}
      {f.recettes.map(x => (
        <View key={x.recetteId} style={s.ligneRecette}>
          <Text style={s.nomRecette} numberOfLines={1}>{nomRecette(x.recetteId)}</Text>
          <Stepper valeur={x.personnes} onMoins={() => majPersonnesRecette(x.recetteId, Math.max(1, x.personnes - 1))}
            onPlus={() => majPersonnesRecette(x.recetteId, x.personnes + 1)} apres={<Text style={s.perServ}>{t('repas.perServ')}</Text>} />
          <TouchableOpacity style={s.retirer} onPress={() => retirerRecette(x.recetteId)}><Text style={s.retirerTxt}>✕</Text></TouchableOpacity>
        </View>
      ))}
      {picker ? (
        disponibles.length === 0
          ? <Text style={s.vide}>{t('repas.toutesAjoutees')}</Text>
          : <ChoixPuces options={disponibles.map(r => ({ id: r.id, label: r.nom || t('repas.sansNom') }))}
              value={null} onChange={ajouterRecette} />
      ) : (
        <TouchableOpacity style={s.ajouter} onPress={() => setPicker(true)}>
          <Text style={s.ajouterTxt}>＋ {t('repas.ajouterRecette')}</Text>
        </TouchableOpacity>
      )}

      <Text style={s.lbl}>{t('repas.extras')}</Text>
      {f.extras.map((e, i) => (
        <View key={i} style={s.ligneIng}>
          <TextInput style={[s.input, { flex: 3 }]} value={e.nom} onChangeText={v => setExtra(i, 'nom', v)}
            placeholder={t('repas.phExtra')} placeholderTextColor={UI.faint} />
          <TextInput style={[s.input, { flex: 1 }]} value={e.quantite} onChangeText={v => setExtra(i, 'quantite', v)}
            placeholder={t('repas.qte')} placeholderTextColor={UI.faint} />
          <TextInput style={[s.input, { flex: 1 }]} value={e.unite} onChangeText={v => setExtra(i, 'unite', v)}
            placeholder={t('repas.unite')} placeholderTextColor={UI.faint} />
          <TextInput style={[s.input, { flex: 1 }]} value={e.prix != null ? String(e.prix) : ''}
            onChangeText={v => setExtra(i, 'prix', v.replace(/[^0-9.,]/g, ''))}
            keyboardType="numbers-and-punctuation" placeholder={t('repas.prix')} placeholderTextColor={UI.faint} />
          <TouchableOpacity style={s.retirer} onPress={() => retirerExtra(i)}><Text style={s.retirerTxt}>✕</Text></TouchableOpacity>
        </View>
      ))}
      <TouchableOpacity style={s.ajouter} onPress={ajouterExtra}>
        <Text style={s.ajouterTxt}>＋ {t('repas.ajouterExtra')}</Text>
      </TouchableOpacity>
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  row: { flexDirection: 'row', alignItems: 'center' },
  apres: { fontSize: 13, color: UI.muted },
  perServ: { fontSize: 11, color: UI.muted },
  emojis: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  emoji: { width: 40, height: 40, borderRadius: 10, alignItems: 'center', justifyContent: 'center', backgroundColor: UI.card, borderWidth: 0.5, borderColor: UI.border },
  emojiSel: { borderWidth: 2, borderColor: UI.accent },
  vide: { fontSize: 13, color: UI.faint, lineHeight: 18, paddingVertical: 8 },
  ligneRecette: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 6 },
  nomRecette: { flex: 1, fontSize: 14, color: UI.ink, fontWeight: '600' },
  ligneIng: { flexDirection: 'row', gap: 6, marginBottom: 6, alignItems: 'center' },
  retirer: { width: 30, height: 30, borderRadius: 8, alignItems: 'center', justifyContent: 'center' },
  retirerTxt: { fontSize: 16, color: UI.danger || '#c0392b' },
  ajouter: { paddingVertical: 9, alignItems: 'center', marginBottom: 14 },
  ajouterTxt: { fontSize: 13.5, fontWeight: '700', color: UI.accent },
});
